/**
 * Display formatting for dates/times. Centralised so views and hooks share one
 * implementation rather than reimplementing `toLocale*` calls.
 */

/** Time of day, e.g. "16:00". */
export const formatTime = (iso: string): string =>
  new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

/**
 * Short date, e.g. "Sat 24 May". Pass `{ omitDay: true }` for "24 May",
 * `{ year: '2-digit' }` for "24 May 26", or
 * `{ omitDay: true, month: 'long', year: 'numeric' }` for "24 May 2024".
 */
export const formatDate = (
  iso: string,
  opts?: {
    omitDay?: boolean;
    month?: 'short' | 'long';
    year?: '2-digit' | 'numeric';
  },
): string =>
  new Date(iso).toLocaleDateString([], {
    ...(opts?.omitDay ? {} : { weekday: 'short' }),
    day: 'numeric',
    month: opts?.month ?? 'short',
    ...(opts?.year ? { year: opts.year } : {}),
  });

const CALENDAR_DATE = /^\d{4}-\d{2}-\d{2}$/;

/** True for a bare "YYYY-MM-DD" date, with no time. */
export const isCalendarDate = (value: string): boolean =>
  CALENDAR_DATE.test(value);

/** "YYYY-MM-DD" in full, e.g. "19 March 1943", whatever the time zone. */
export const formatCalendarDate = (date: string): string =>
  new Date(`${date}T00:00:00Z`).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });

/** Date and time, e.g. "Sat 24 May · 16:00". */
export const formatDateTime = (iso: string): string =>
  `${formatDate(iso)} · ${formatTime(iso)}`;

/**
 * Conversions between an ISO timestamp and the local `YYYY-MM-DD` / `HH:MM`
 * strings used by native date/time inputs. Kept here so the presentational
 * form (llama) takes raw input strings and the wiring owns the ISO mapping.
 */

/** "YYYY-MM-DD" from `<input type="date">` → ISO at local midnight. */
export const fromDateInput = (dateInput: string): string =>
  new Date(`${dateInput}T00:00:00`).toISOString();

/** ISO → "YYYY-MM-DD" in local time (for `<input type="date">`). */
export const toDateInput = (iso: string | null | undefined): string => {
  if (!iso) return '';
  const d = new Date(iso);
  const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 10);
};

/** ISO → "HH:MM" in local time (for `<input type="time">`). */
export const toTimeInput = (iso: string | null | undefined): string => {
  if (!iso) return '';
  const d = new Date(iso);
  const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return local.toISOString().slice(11, 16);
};

/** Combine a local date + time into an ISO string, or null when no date. */
export const combineDateTime = (date: string, time: string): string | null => {
  if (!date) return null;
  return new Date(`${date}T${time || '00:00'}`).toISOString();
};

/**
 * Day of month with 24h time, e.g. "24 May at 16:00".
 * Used for call/event timestamps in the member app.
 */
export const formatDayMonthTime = (iso: string): string =>
  `${formatDate(iso, { omitDay: true })} at ${formatTime(iso)}`;

/** Date object → "YYYY-MM-DD" in local time. */
export const toLocalDateStr = (date: Date): string => {
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 10);
};

/** End of the current week (Sunday) as a Date. */
export const getEndOfWeek = (date: Date): Date => {
  const day = date.getDay();
  const end = new Date(date);
  end.setDate(end.getDate() + (day === 0 ? 0 : 7 - day));
  return end;
};

/** Seconds as MM:SS or H:MM:SS, e.g. 125 → "02:05", 3661 → "1:01:01". */
export const formatDuration = (totalSeconds: number): string => {
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  const mm = String(m).padStart(2, '0');
  const ss = String(s).padStart(2, '0');
  return h > 0 ? `${h}:${mm}:${ss}` : `${mm}:${ss}`;
};

/** Current time as ISO string, for passing to format helpers. */
export const getTimestampISO = (): string => new Date().toISOString();

type RecurrenceFrequency = 'daily' | 'weekly' | 'monthly';

const FREQ_LABELS: Record<RecurrenceFrequency, string> = {
  daily: 'day',
  weekly: 'week',
  monthly: 'month',
};

export const formatRecurrence = (
  frequency: RecurrenceFrequency,
  interval: number,
): string => {
  const unit = FREQ_LABELS[frequency] ?? frequency;
  return interval === 1 ? `Every ${unit}` : `Every ${interval} ${unit}s`;
};
